import React, { useCallback, useEffect, useRef, useState } from "react";
import { CircularProgress } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import TextFieldsIcon from "@mui/icons-material/TextFields";
import DrawIcon from "@mui/icons-material/Draw";
import HighlightIcon from "@mui/icons-material/Highlight";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import CheckBoxOutlinedIcon from "@mui/icons-material/CheckBoxOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import UndoIcon from "@mui/icons-material/Undo";
import RedoIcon from "@mui/icons-material/Redo";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import PanToolAltIcon from "@mui/icons-material/PanToolAlt";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import { Document, Page, pdfjs } from "react-pdf";
import { PDFDocument, rgb, StandardFonts } from "pdf-lib";
import { toast } from "react-toastify";
import { CaseServices } from "../../../services/CaseServices";
import { AuthServices } from "../../../services/AuthServices";
import { handleApiErrorResponses } from "../../../utils/ApisErrorHandling";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const TOOLS = {
  SELECT: "select",
  TEXT: "text",
  DRAW: "draw",
  HIGHLIGHT: "highlight",
  WHITEOUT: "whiteout",
  CHECKBOX: "checkbox",
  SIGNATURE: "signature",
  PHOTO: "photo",
};

const ANN_STORAGE_PREFIX = "tpaFillAnnotations:";

const loadSavedFillSession = (docId) => {
  if (!docId || typeof localStorage === "undefined") return null;
  try {
    const raw = localStorage.getItem(`${ANN_STORAGE_PREFIX}${docId}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed?.annotations)) return null;
    return parsed;
  } catch {
    return null;
  }
};

const persistFillSession = (docId, annotations, preferRaster) => {
  if (!docId || typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(
      `${ANN_STORAGE_PREFIX}${docId}`,
      JSON.stringify({
        annotations,
        preferRaster: !!preferRaster,
        savedAt: Date.now(),
      }),
    );
  } catch (err) {
    console.warn("Could not persist fill-template annotations", err);
  }
};

/** Free standard PDF fonts (pdf-lib) + matching CSS stacks for the overlay. */
const FONT_OPTIONS = [
  {
    id: "helvetica",
    label: "Helvetica",
    css: "Helvetica, Arial, sans-serif",
    pdf: StandardFonts.Helvetica,
  },
  {
    id: "times",
    label: "Times",
    css: '"Times New Roman", Times, serif',
    pdf: StandardFonts.TimesRoman,
  },
  {
    id: "courier",
    label: "Courier",
    css: '"Courier New", Courier, monospace',
    pdf: StandardFonts.Courier,
  },
];

const getFontOption = (id) =>
  FONT_OPTIONS.find((f) => f.id === id) || FONT_OPTIONS[0];

const makeId = () =>
  `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

/**
 * Detect real file type from the URL.
 * Document names often end with ".pdf" even when each page URL is a JPEG/PNG
 * (common after upload-images). Prefer URL extension over documentName.
 */
const isPdfDocumentUrl = (url, extraName = "") => {
  if (!url && !extraName) return false;
  const urlPath = String(url || "")
    .split("?")[0]
    .split("#")[0]
    .toLowerCase();
  if (/\.(jpe?g|png|gif|webp|bmp)$/i.test(urlPath)) return false;
  if (urlPath.endsWith(".pdf")) return true;
  // No clear URL extension — only then consider the display name
  const name = String(extraName || "").toLowerCase();
  if (/\.(jpe?g|png|gif|webp|bmp)$/i.test(name)) return false;
  if (!/\.\w+$/.test(urlPath) && name.endsWith(".pdf")) return true;
  return false;
};

const getDocPages = (doc) =>
  (doc?.documenturlmasters || []).filter(
    (u) => u?.documentUrl && u?.deleted !== true,
  );

const isPreauthTemplateDocName = (doc) => {
  const name = String(doc?.documentName || "")
    .trim()
    .toLowerCase();
  return name === "preauth template" || name === "template";
};

/** Start compact (~15px wide); grow wide enough that typed text stays on one line. */
const MIN_TEXT_WIDTH_PX = 15;

let _measureCanvas;
const measureTextWidthPx = (text, fontSize, fontFamilyCss, letterSpacing = 0) => {
  if (typeof document === "undefined") {
    return Math.max(MIN_TEXT_WIDTH_PX, String(text || "").length * fontSize * 0.7);
  }
  if (!_measureCanvas) _measureCanvas = document.createElement("canvas");
  const ctx = _measureCanvas.getContext("2d");
  const size = fontSize || 10;
  ctx.font = `${size}px ${fontFamilyCss || "Helvetica, Arial, sans-serif"}`;
  const lines = String(text || "").split("\n");
  let max = MIN_TEXT_WIDTH_PX;
  const spacing = Number(letterSpacing) || 0;
  lines.forEach((line) => {
    const metrics = ctx.measureText(line || " ");
    const spaced =
      metrics.width + Math.max(0, (line || "").length - 1) * spacing;
    // Extra pad so caret / last glyph never wraps
    max = Math.max(max, spaced + size * 0.85 + 8);
  });
  return max;
};

const estimateTextWidthPct = (
  text,
  fontSize,
  pageWidthPx,
  letterSpacing = 0,
  fontFamilyCss = "Helvetica, Arial, sans-serif",
) => {
  const contentPx = measureTextWidthPx(
    text,
    fontSize || 10,
    fontFamilyCss,
    letterSpacing,
  );
  return clamp(contentPx / Math.max(pageWidthPx, 1), 0.01, 0.95);
};

const estimateTextHeightPct = (text, fontSize, pageWidthPx) => {
  const size = fontSize || 10;
  const lines = Math.max(1, String(text || "").split("\n").length);
  // Enough room for ascenders/descenders + caret (was fixed 16px and clipped)
  const linePx = Math.ceil(size * 1.45) + 2;
  const px = lines * linePx;
  const pageHeightPx = pageWidthPx * 1.414;
  return clamp(px / pageHeightPx, 0.012, 0.5);
};

/**
 * In-app TPA template filler (react-pdf + pdf-lib).
 * Interaction model closer to HTMLSuite: pick a tool, work directly on the page,
 * then move/resize selected items. Undo/redo + zoom included.
 */

/**
 * Rebuild previously filled Preauth pages into a PDF.
 * S3 accelerate URLs block browser fetch/canvas (no CORS) — use existing
 * backend /load-image (same path as Download Multiple Documents).
 */
const loadBytesFromExistingPreauth = async (preauthDocument) => {
  const pages = getDocPages(preauthDocument);
  if (!pages.length) return null;

  const imagePages = pages.filter((p) => !isPdfDocumentUrl(p.documentUrl));

  // Typical Fill Template save: JPEG/PNG pages via upload-images
  if (imagePages.length > 0) {
    const images = imagePages.map((page) => ({
      image: page.documentUrl,
      compressionPossible: false,
    }));
    const response = await AuthServices.loadImagesBase64(
      { images },
      () => {},
      () => {},
    );
    const blob = response?.data;
    if (!blob) throw new Error("Empty response from load-image");
    return new Uint8Array(await blob.arrayBuffer());
  }

  // Single real PDF uploaded as one URL — try browser fetch (may still hit CORS)
  if (pages.length === 1 && isPdfDocumentUrl(pages[0].documentUrl)) {
    const res = await fetch(pages[0].documentUrl);
    if (!res.ok) throw new Error("Failed to fetch previously filled PDF");
    return new Uint8Array(await res.arrayBuffer());
  }

  return null;
};

const FillTemplateModal = ({
  open,
  onClose,
  payer,
  preauthDocumentId,
  preauthDocument = null,
  onUploaded,
  caseDocuments = [],
}) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [templateUrl, setTemplateUrl] = useState("");
  const [templateBytes, setTemplateBytes] = useState(null);
  /** Prior Preauth JPEG/PNG pages — shown immediately (no /load-image wait). */
  const [imageTemplatePages, setImageTemplatePages] = useState([]);
  const [numPages, setNumPages] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [baseWidth, setBaseWidth] = useState(640);
  const [tool, setTool] = useState(TOOLS.TEXT);
  const [annotations, setAnnotations] = useState([]);
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [signatureOpen, setSignatureOpen] = useState(false);
  const [drawColor, setDrawColor] = useState("#111111");
  const [drawStrokeWidth, setDrawStrokeWidth] = useState(2); // 1–8 px-ish scale
  const [drawOpacity, setDrawOpacity] = useState(1); // 0.25–1 lighten stroke
  const [checkboxSizePct, setCheckboxSizePct] = useState(0.016);
  const [highlightColor, setHighlightColor] = useState("#FFE566");
  const [defaultFontFamily, setDefaultFontFamily] = useState("helvetica");
  const [defaultFontSize, setDefaultFontSize] = useState(10);
  const [defaultLetterSpacing, setDefaultLetterSpacing] = useState(0);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [showDocsPanel, setShowDocsPanel] = useState(true);
  const [refDocId, setRefDocId] = useState(null);
  const [refPageIdx, setRefPageIdx] = useState(0);
  const [refPdfPageCount, setRefPdfPageCount] = useState(0);
  const [refZoom, setRefZoom] = useState(1);

  const signatureCanvasRef = useRef(null);
  const drawingRef = useRef(false);
  const photoInputRef = useRef(null);
  const strokeRef = useRef(null);
  const dragRef = useRef(null);
  const pageRefs = useRef({});
  const scrollContainerRef = useRef(null);
  const currentPageIndexRef = useRef(0);
  const objectUrlRef = useRef(null);
  /** Resolves to ArrayBuffer/Uint8Array for prior-fill save; filled in background. */
  const bytesReadyPromiseRef = useRef(null);
  const templateBytesRef = useRef(null);
  /** True when editor base is burned Preauth images (not blank payer PDF). */
  const loadedAsRasterRef = useRef(false);

  const pageWidth = Math.round(baseWidth * zoom);

  const pushHistory = useCallback((next) => {
    setUndoStack((prev) => [...prev.slice(-49), annotations]);
    setRedoStack([]);
    setAnnotations(next);
  }, [annotations]);

  const resetState = useCallback(() => {
    setLoading(true);
    setSaving(false);
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
    bytesReadyPromiseRef.current = null;
    templateBytesRef.current = null;
    loadedAsRasterRef.current = false;
    setTemplateUrl("");
    setTemplateBytes(null);
    setImageTemplatePages([]);
    setNumPages(0);
    setZoom(1);
    setTool(TOOLS.TEXT);
    setAnnotations([]);
    setUndoStack([]);
    setRedoStack([]);
    setSelectedId(null);
    setSignatureOpen(false);
    setCurrentPageIndex(0);
    currentPageIndexRef.current = 0;
    setShowDocsPanel(true);
    setRefDocId(null);
    setRefPageIdx(0);
    setRefZoom(1);
  }, []);

  const viewableDocs = (caseDocuments || []).filter(
    (d) => getDocPages(d).length > 0 && !isPreauthTemplateDocName(d),
  );
  const activeRefDoc =
    viewableDocs.find((d) => d.id === refDocId) || viewableDocs[0] || null;
  const refPages = getDocPages(activeRefDoc);
  // For a single multi-page PDF URL, page through PDF pages; otherwise page urlmasters
  const activeRefIsPdfFile =
    refPages.length === 1 &&
    isPdfDocumentUrl(refPages[0]?.documentUrl);
  const refTotalPages = activeRefIsPdfFile
    ? Math.max(refPdfPageCount, 1)
    : refPages.length;
  const activeRefPage = activeRefIsPdfFile
    ? refPages[0]
    : refPages[Math.min(refPageIdx, Math.max(refPages.length - 1, 0))] ||
      null;
  const activeRefIsPdf = isPdfDocumentUrl(activeRefPage?.documentUrl);

  useEffect(() => {
    if (!open) return;
    if (!viewableDocs.length) {
      setRefDocId(null);
      setRefPageIdx(0);
      return;
    }
    const stillValid = viewableDocs.some((d) => d.id === refDocId);
    if (!stillValid) {
      setRefDocId(viewableDocs[0].id);
      setRefPageIdx(0);
      setRefPdfPageCount(0);
    }
  }, [open, caseDocuments, refDocId, viewableDocs.length]);

  const initSignatureCanvas = useCallback((canvas) => {
    signatureCanvasRef.current = canvas;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = "#111111";
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
  }, []);

  useEffect(() => {
    if (!open) return;
    resetState();

    const storeTemplateBytes = (existingBytes) => {
      const uint8 =
        existingBytes instanceof Uint8Array
          ? existingBytes
          : new Uint8Array(existingBytes);
      const ab = uint8.buffer.slice(
        uint8.byteOffset,
        uint8.byteOffset + uint8.byteLength,
      );
      templateBytesRef.current = ab;
      setTemplateBytes(ab);
      return { uint8, ab };
    };

    const applyPdfBytes = (existingBytes, infoToast) => {
      const { uint8 } = storeTemplateBytes(existingBytes);
      const objUrl = URL.createObjectURL(
        new Blob([uint8], { type: "application/pdf" }),
      );
      objectUrlRef.current = objUrl;
      setTemplateUrl(objUrl);
      if (infoToast) toast.info(infoToast);
    };

    const loadBlankPayerTemplate = async () => {
      const res = await CaseServices.getTpaTemplate(payer);
      const url = res?.data?.list?.[0]?.templateUrl;
      if (!url) {
        toast.error("No template found for this TPA");
        return false;
      }
      // Show in viewer immediately (react-pdf can load by URL)
      setTemplateUrl(url);
      const pdfRes = await fetch(url);
      if (!pdfRes.ok) throw new Error("Failed to fetch template PDF");
      const buffer = await pdfRes.arrayBuffer();
      storeTemplateBytes(buffer.slice(0));
      return true;
    };

    const loadTemplate = async () => {
      if (!payer) {
        toast.error("Payer not found for this case");
        setLoading(false);
        return;
      }
      try {
        const existingPages = getDocPages(preauthDocument);
        const imageUrls = existingPages
          .filter((p) => !isPdfDocumentUrl(p.documentUrl))
          .map((p) => p.documentUrl);
        const savedSession = loadSavedFillSession(preauthDocumentId);

        // Prefer blank payer PDF + restored editable annotations (Select → Delete works).
        if (
          savedSession?.annotations?.length &&
          !savedSession.preferRaster
        ) {
          const ok = await loadBlankPayerTemplate();
          if (ok) {
            setAnnotations(savedSession.annotations);
            toast.info(
              "Previous fill restored as editable fields. Select an item, then Delete to remove or change it.",
            );
            setLoading(false);
            return;
          }
        }

        // Raster path: prior uploaded page images (marks baked in).
        // Overlay annotations from last session remain editable; use Whiteout for baked marks.
        if (imageUrls.length > 0) {
          loadedAsRasterRef.current = true;
          setImageTemplatePages(imageUrls);
          setNumPages(imageUrls.length);
          if (savedSession?.annotations?.length) {
            setAnnotations(savedSession.annotations);
          }
          setLoading(false);
          toast.info(
            savedSession?.annotations?.length
              ? "Previous form loaded. Editable marks can be deleted; use Whiteout to cover baked-in text/checks."
              : "Previous form loaded as image. Use Whiteout to cover mistakes, then add new fields. After Save, future edits stay fully editable.",
          );
          const bytesPromise = loadBytesFromExistingPreauth(preauthDocument)
            .then((existingBytes) => {
              if (!existingBytes) {
                throw new Error("Empty prior-fill PDF");
              }
              storeTemplateBytes(existingBytes);
              return existingBytes;
            })
            .catch((existingErr) => {
              console.warn(
                "Background prior-fill PDF prepare failed:",
                existingErr,
              );
              return null;
            });
          bytesReadyPromiseRef.current = bytesPromise;
          return;
        }

        // Single prior PDF page URL
        if (existingPages.length > 0) {
          try {
            const existingBytes = await loadBytesFromExistingPreauth(
              preauthDocument,
            );
            if (existingBytes) {
              loadedAsRasterRef.current = true;
              applyPdfBytes(
                existingBytes,
                "Previously filled form loaded. Add or change details, then Save & Upload again.",
              );
              if (savedSession?.annotations?.length) {
                setAnnotations(savedSession.annotations);
              }
              setLoading(false);
              return;
            }
          } catch (existingErr) {
            console.warn(
              "Could not reload previously filled template:",
              existingErr,
            );
            toast.warn(
              "Could not reload previous fill. Opening blank template — you can fill again.",
            );
          }
        }

        await loadBlankPayerTemplate();
        if (savedSession?.annotations?.length) {
          setAnnotations(savedSession.annotations);
        }
      } catch (error) {
        handleApiErrorResponses(error);
        toast.error(
          "Could not load template. Check that the PDF URL allows browser access.",
        );
      } finally {
        setLoading(false);
      }
    };

    loadTemplate();
  }, [open, payer, preauthDocument, preauthDocumentId, resetState]);

  useEffect(() => {
    const updateWidth = () => {
      // Fill pane is ~60% when docs panel is open, else nearly full width
      const usable = showDocsPanel
        ? window.innerWidth * 0.6 - 48
        : window.innerWidth - 80;
      setBaseWidth(Math.min(780, Math.max(280, usable)));
    };
    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, [showDocsPanel]);

  // Track which PDF page is currently in view (for signature/photo placement)
  useEffect(() => {
    if (!open || !numPages) return undefined;
    const root = scrollContainerRef.current;
    if (!root) return undefined;

    const ratios = {};
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const idx = Number(entry.target.getAttribute("data-page"));
          if (Number.isNaN(idx)) return;
          ratios[idx] = entry.isIntersecting ? entry.intersectionRatio : 0;
        });
        let bestIdx = currentPageIndexRef.current;
        let bestRatio = -1;
        Object.keys(ratios).forEach((key) => {
          const idx = Number(key);
          if (ratios[idx] > bestRatio) {
            bestRatio = ratios[idx];
            bestIdx = idx;
          }
        });
        if (bestRatio > 0 && bestIdx !== currentPageIndexRef.current) {
          currentPageIndexRef.current = bestIdx;
          setCurrentPageIndex(bestIdx);
        }
      },
      { root, threshold: [0.1, 0.25, 0.4, 0.55, 0.7, 0.85, 1] },
    );

    const observePages = () => {
      Object.values(pageRefs.current).forEach((el) => {
        if (el) observer.observe(el);
      });
    };
    observePages();
    // Pages mount slightly after numPages is set
    const t = window.setTimeout(observePages, 300);

    return () => {
      window.clearTimeout(t);
      observer.disconnect();
    };
  }, [open, numPages, templateUrl, imageTemplatePages, pageWidth]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z") {
        e.preventDefault();
        if (e.shiftKey) {
          setRedoStack((r) => {
            if (!r.length) return r;
            const next = r[r.length - 1];
            setUndoStack((u) => [...u, annotations]);
            setAnnotations(next);
            return r.slice(0, -1);
          });
        } else {
          setUndoStack((u) => {
            if (!u.length) return u;
            const prev = u[u.length - 1];
            setRedoStack((r) => [...r, annotations]);
            setAnnotations(prev);
            return u.slice(0, -1);
          });
        }
      }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "y") {
        e.preventDefault();
        setRedoStack((r) => {
          if (!r.length) return r;
          const next = r[r.length - 1];
          setUndoStack((u) => [...u, annotations]);
          setAnnotations(next);
          return r.slice(0, -1);
        });
      }
      if (e.key === "Delete" || e.key === "Backspace") {
        const tag = document.activeElement?.tagName;
        if (tag === "INPUT" || tag === "TEXTAREA") return;
        if (!selectedId) return;
        e.preventDefault();
        pushHistory(annotations.filter((a) => a.id !== selectedId));
        setSelectedId(null);
      }
      if (e.key === "Escape") {
        setSelectedId(null);
        setTool(TOOLS.SELECT);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, annotations, selectedId, pushHistory]);

  if (!open) return null;

  const getPagePoint = (e, pageEl) => {
    const rect = pageEl.getBoundingClientRect();
    return {
      xPct: clamp((e.clientX - rect.left) / rect.width, 0, 1),
      yPct: clamp((e.clientY - rect.top) / rect.height, 0, 1),
      rect,
    };
  };

  const placeImageAnnotation = (type, imageDataUrl) => {
    // Prefer the page the user is currently viewing in the scroll area
    const pageIndex = clamp(
      currentPageIndexRef.current || 0,
      0,
      Math.max(0, numPages - 1),
    );
    const id = makeId();
    const next = [
      ...annotations,
      {
        id,
        type,
        pageIndex,
        xPct: 0.35,
        yPct: 0.4,
        widthPct: type === "signature" ? 0.28 : 0.22,
        heightPct: type === "signature" ? 0.1 : 0.2,
        imageDataUrl,
      },
    ];
    pushHistory(next);
    setSelectedId(id);
    setTool(TOOLS.SELECT);
    toast.success(
      type === "signature"
        ? `Signature placed on page ${pageIndex + 1} — drag to position`
        : `Photo placed on page ${pageIndex + 1} — drag to position`,
    );
  };

  const handlePagePointerDown = (e, pageIndex) => {
    if (e.button !== 0) return;
    // Remember the page the user is interacting with
    currentPageIndexRef.current = pageIndex;
    setCurrentPageIndex(pageIndex);
    const pageEl = e.currentTarget;
    const { xPct, yPct } = getPagePoint(e, pageEl);

    if (tool === TOOLS.SELECT) {
      if (!e.target.closest("[data-annotation]")) setSelectedId(null);
      return;
    }

    if (tool === TOOLS.TEXT) {
      e.preventDefault();
      const id = makeId();
      const fontSize = defaultFontSize;
      const letterSpacing = defaultLetterSpacing;
      // Tiny default box — expands as the user types
      pushHistory([
        ...annotations,
        {
          id,
          type: "text",
          pageIndex,
          xPct,
          yPct,
          widthPct: estimateTextWidthPct(
            "",
            fontSize,
            pageWidth,
            letterSpacing,
            getFontOption(defaultFontFamily).css,
          ),
          heightPct: estimateTextHeightPct("", fontSize, pageWidth),
          text: "",
          fontSize,
          fontFamily: defaultFontFamily,
          letterSpacing,
          // Keep size relative to page so zoom / export stay aligned
          fontSizePct: fontSize / Math.max(pageWidth, 1),
          autoSize: true,
        },
      ]);
      setSelectedId(id);
      // keep TEXT tool so doctor can keep adding fields quickly
      return;
    }

    if (tool === TOOLS.CHECKBOX) {
      e.preventDefault();
      const id = makeId();
      // Place checkbox using current size; click again to toggle
      const sizePct = checkboxSizePct;
      pushHistory([
        ...annotations,
        {
          id,
          type: "checkbox",
          pageIndex,
          xPct: xPct - sizePct / 2,
          yPct: yPct - sizePct / 2,
          widthPct: sizePct,
          heightPct: sizePct,
          checked: true,
        },
      ]);
      setSelectedId(id);
      return;
    }

    if (tool === TOOLS.DRAW) {
      e.preventDefault();
      pageEl.setPointerCapture?.(e.pointerId);
      const id = makeId();
      // Map UI thickness 1–8 → page % stroke width
      const strokeWidthPct = 0.0015 + drawStrokeWidth * 0.0018;
      strokeRef.current = {
        id,
        pageIndex,
        color: drawColor,
        points: [{ xPct, yPct }],
      };
      setAnnotations((prev) => [
        ...prev,
        {
          id,
          type: "draw",
          pageIndex,
          color: drawColor,
          opacity: drawOpacity,
          points: [{ xPct, yPct }],
          strokeWidthPct,
        },
      ]);
      setSelectedId(id);

      const onMove = (ev) => {
        const pt = getPagePoint(ev, pageEl);
        const sid = strokeRef.current?.id;
        if (!sid) return;
        setAnnotations((prev) =>
          prev.map((a) =>
            a.id === sid
              ? { ...a, points: [...a.points, { xPct: pt.xPct, yPct: pt.yPct }] }
              : a,
          ),
        );
      };
      const onUp = () => {
        const finished = strokeRef.current;
        strokeRef.current = null;
        pageEl.releasePointerCapture?.(e.pointerId);
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        if (finished) {
          setUndoStack((u) => [...u.slice(-49), annotations]);
          setRedoStack([]);
        }
      };
      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
      return;
    }

    if (tool === TOOLS.HIGHLIGHT || tool === TOOLS.WHITEOUT) {
      e.preventDefault();
      pageEl.setPointerCapture?.(e.pointerId);
      const id = makeId();
      const isWhiteout = tool === TOOLS.WHITEOUT;
      strokeRef.current = {
        id,
        pageIndex,
        startX: xPct,
        startY: yPct,
      };
      setAnnotations((prev) => [
        ...prev,
        {
          id,
          type: isWhiteout ? "whiteout" : "highlight",
          pageIndex,
          xPct,
          yPct,
          widthPct: 0.01,
          heightPct: 0.01,
          color: isWhiteout ? "#FFFFFF" : highlightColor,
        },
      ]);
      setSelectedId(id);

      const onMove = (ev) => {
        const pt = getPagePoint(ev, pageEl);
        const s = strokeRef.current;
        if (!s) return;
        const nx = Math.min(s.startX, pt.xPct);
        const ny = Math.min(s.startY, pt.yPct);
        const nw = Math.abs(pt.xPct - s.startX);
        const nh = Math.abs(pt.yPct - s.startY);
        setAnnotations((prev) =>
          prev.map((a) =>
            a.id === s.id
              ? { ...a, xPct: nx, yPct: ny, widthPct: nw, heightPct: nh }
              : a,
          ),
        );
      };
      const onUp = () => {
        strokeRef.current = null;
        pageEl.releasePointerCapture?.(e.pointerId);
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        setUndoStack((u) => [...u.slice(-49), annotations]);
        setRedoStack([]);
      };
      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
    }
  };

  const toggleCheckbox = (id) => {
    const snapshot = annotations;
    setAnnotations((prev) =>
      prev.map((a) =>
        a.id === id && a.type === "checkbox"
          ? { ...a, checked: !a.checked }
          : a,
      ),
    );
    setUndoStack((u) => [...u.slice(-49), snapshot]);
    setRedoStack([]);
  };

  const startDrag = (e, id) => {
    if (tool !== TOOLS.SELECT && tool !== TOOLS.TEXT && tool !== TOOLS.CHECKBOX)
      return;
    e.stopPropagation();
    e.preventDefault();
    setSelectedId(id);
    const ann = annotations.find((a) => a.id === id);
    if (!ann || ann.type === "draw") return;
    const pageEl = e.currentTarget.closest("[data-page]");
    if (!pageEl) return;
    const rect = pageEl.getBoundingClientRect();
    dragRef.current = {
      mode: "move",
      id,
      startX: e.clientX,
      startY: e.clientY,
      origX: ann.xPct,
      origY: ann.yPct,
      origW: ann.widthPct || 0.2,
      origH: ann.heightPct || 0.08,
      pageW: rect.width,
      pageH: rect.height,
    };

    const snapshot = annotations;
    const onMove = (ev) => {
      const d = dragRef.current;
      if (!d || d.mode !== "move") return;
      const dx = (ev.clientX - d.startX) / d.pageW;
      const dy = (ev.clientY - d.startY) / d.pageH;
      setAnnotations((prev) =>
        prev.map((a) =>
          a.id === d.id
            ? {
                ...a,
                xPct: clamp(d.origX + dx, 0, 0.98),
                yPct: clamp(d.origY + dy, 0, 0.98),
              }
            : a,
        ),
      );
    };
    const onUp = () => {
      dragRef.current = null;
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      setUndoStack((u) => [...u.slice(-49), snapshot]);
      setRedoStack([]);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  const startResize = (e, id) => {
    e.stopPropagation();
    e.preventDefault();
    setSelectedId(id);
    const ann = annotations.find((a) => a.id === id);
    if (!ann) return;
    // Manual resize turns off auto-grow for text boxes
    if (ann.type === "text") {
      setAnnotations((prev) =>
        prev.map((a) => (a.id === id ? { ...a, autoSize: false } : a)),
      );
    }
    const pageEl = e.currentTarget.closest("[data-page]");
    if (!pageEl) return;
    const rect = pageEl.getBoundingClientRect();
    dragRef.current = {
      mode: "resize",
      id,
      startX: e.clientX,
      startY: e.clientY,
      origW: ann.widthPct || 0.2,
      origH: ann.heightPct || 0.08,
      pageW: rect.width,
      pageH: rect.height,
    };
    const snapshot = annotations;
    const onMove = (ev) => {
      const d = dragRef.current;
      if (!d || d.mode !== "resize") return;
      const dx = (ev.clientX - d.startX) / d.pageW;
      const dy = (ev.clientY - d.startY) / d.pageH;
      setAnnotations((prev) =>
        prev.map((a) =>
          a.id === d.id
            ? {
                ...a,
                widthPct: clamp(d.origW + dx, 0.04, 0.95),
                heightPct: clamp(d.origH + dy, 0.02, 0.95),
              }
            : a,
        ),
      );
    };
    const onUp = () => {
      dragRef.current = null;
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      setUndoStack((u) => [...u.slice(-49), snapshot]);
      setRedoStack([]);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  const undo = () => {
    setUndoStack((u) => {
      if (!u.length) return u;
      const prev = u[u.length - 1];
      setRedoStack((r) => [...r, annotations]);
      setAnnotations(prev);
      return u.slice(0, -1);
    });
  };

  const redo = () => {
    setRedoStack((r) => {
      if (!r.length) return r;
      const next = r[r.length - 1];
      setUndoStack((u) => [...u, annotations]);
      setAnnotations(next);
      return r.slice(0, -1);
    });
  };

  const deleteSelected = () => {
    // Prefer deleting the selected item (including empty text boxes).
    // If nothing is selected, clear any leftover empty text boxes.
    if (selectedId) {
      pushHistory(annotations.filter((a) => a.id !== selectedId));
      setSelectedId(null);
      return;
    }
    const withoutEmptyText = annotations.filter(
      (a) => !(a.type === "text" && !String(a.text || "").trim()),
    );
    if (withoutEmptyText.length === annotations.length) return;
    pushHistory(withoutEmptyText);
  };

  const signaturePointer = (e, type) => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * canvas.width;
    const y = ((e.clientY - rect.top) / rect.height) * canvas.height;
    if (type === "down") {
      drawingRef.current = true;
      ctx.beginPath();
      ctx.moveTo(x, y);
      return;
    }
    if (type === "move" && drawingRef.current) {
      ctx.lineTo(x, y);
      ctx.stroke();
      return;
    }
    if (type === "up") drawingRef.current = false;
  };

  const clearSignature = () => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  };

  const insertSignature = () => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    // make white bg transparent-ish by exporting as png of drawn content
    const dataUrl = canvas.toDataURL("image/png");
    setSignatureOpen(false);
    placeImageAnnotation("signature", dataUrl);
  };

  const handlePhotoPick = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!/^image\/(png|jpe?g)$/i.test(file.type)) {
      toast.error("Please upload a PNG or JPG photo");
      return;
    }
    if (file.size > 15 * 1024 * 1024) {
      toast.error("Photo must be smaller than 15 MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => placeImageAnnotation("photo", reader.result);
    reader.readAsDataURL(file);
  };

  const dataUrlToUint8Array = async (dataUrl) => {
    const res = await fetch(dataUrl);
    const buf = await res.arrayBuffer();
    return new Uint8Array(buf);
  };

  const hexToRgb = (hex) => {
    const h = hex.replace("#", "");
    const full =
      h.length === 3
        ? h
            .split("")
            .map((c) => c + c)
            .join("")
        : h;
    const n = parseInt(full, 16);
    return {
      r: ((n >> 16) & 255) / 255,
      g: ((n >> 8) & 255) / 255,
      b: (n & 255) / 255,
    };
  };

  const applyTextStyle = (patch) => {
    if (!selectedId) {
      if (patch.fontFamily != null) setDefaultFontFamily(patch.fontFamily);
      if (patch.fontSize != null) setDefaultFontSize(patch.fontSize);
      if (patch.letterSpacing != null)
        setDefaultLetterSpacing(patch.letterSpacing);
      return;
    }
    setAnnotations((prev) =>
      prev.map((a) => {
        if (a.id !== selectedId || a.type !== "text") return a;
        const fontSize = patch.fontSize != null ? patch.fontSize : a.fontSize;
        const fontFamily =
          patch.fontFamily != null ? patch.fontFamily : a.fontFamily;
        const letterSpacing =
          patch.letterSpacing != null
            ? patch.letterSpacing
            : a.letterSpacing ?? 0;
        const fontSizePct = fontSize / Math.max(pageWidth, 1);
        const next = {
          ...a,
          fontSize,
          fontFamily,
          letterSpacing,
          fontSizePct,
        };
        if (a.autoSize === false) return next;
        return {
          ...next,
          widthPct: estimateTextWidthPct(
            a.text,
            fontSize,
            pageWidth,
            letterSpacing,
            getFontOption(fontFamily).css,
          ),
          heightPct: estimateTextHeightPct(a.text, fontSize, pageWidth),
        };
      }),
    );
    if (patch.fontFamily != null) setDefaultFontFamily(patch.fontFamily);
    if (patch.fontSize != null) setDefaultFontSize(patch.fontSize);
    if (patch.letterSpacing != null)
      setDefaultLetterSpacing(patch.letterSpacing);
  };

  const buildFilledPdfBytesWith = async (bytes) => {
    const source = bytes || templateBytesRef.current || templateBytes;
    if (!source) throw new Error("Template bytes missing");
    const pdfDoc = await PDFDocument.load(source.slice(0));
    const fontCache = {};
    for (const opt of FONT_OPTIONS) {
      fontCache[opt.id] = await pdfDoc.embedFont(opt.pdf);
    }
    const pages = pdfDoc.getPages();

    for (const ann of annotations) {
      const page = pages[ann.pageIndex];
      if (!page) continue;
      const { width, height } = page.getSize();

      if (ann.type === "text" && ann.text?.trim()) {
        // Scale CSS px font (from on-screen page width) → PDF points so
        // review matches what the doctor saw while filling.
        const displayW = Math.max(pageWidth, 1);
        const size =
          (ann.fontSizePct != null
            ? ann.fontSizePct
            : (ann.fontSize || 13) / displayW) * width;
        const font =
          fontCache[ann.fontFamily] || fontCache.helvetica;
        const x = ann.xPct * width;
        const top = ann.yPct * height;
        const lineHeight = size * 1.35;
        // Match CSS top-left box: first baseline sits ~0.9em below top
        const letterSpacingPdf =
          ((ann.letterSpacing ?? 0) / displayW) * width;
        const fontSizePdf = Math.max(4, size);
        const lines = String(ann.text).replace(/\r/g, "").split("\n");
        lines.forEach((line, i) => {
          const content = line;
          if (!content) return;
          const baselineFromTop = size * 0.9 + i * lineHeight;
          const y = height - top - baselineFromTop;
          if (!letterSpacingPdf) {
            page.drawText(content, {
              x,
              y,
              size: fontSizePdf,
              font,
              color: rgb(0.05, 0.05, 0.05),
            });
            return;
          }
          // pdf-lib has no letterSpacing — draw glyphs with extra advance
          let cursorX = x;
          for (const char of content) {
            page.drawText(char, {
              x: cursorX,
              y,
              size: fontSizePdf,
              font,
              color: rgb(0.05, 0.05, 0.05),
            });
            cursorX +=
              font.widthOfTextAtSize(char, fontSizePdf) + letterSpacingPdf;
          }
        });
      } else if (ann.type === "highlight" || ann.type === "whiteout") {
        const c = hexToRgb(
          ann.type === "whiteout" ? "#FFFFFF" : ann.color || "#FFE566",
        );
        page.drawRectangle({
          x: ann.xPct * width,
          y: height - (ann.yPct + ann.heightPct) * height,
          width: ann.widthPct * width,
          height: ann.heightPct * height,
          color: rgb(c.r, c.g, c.b),
          opacity: ann.type === "whiteout" ? 1 : 0.35,
          borderWidth: 0,
        });
      } else if (ann.type === "checkbox") {
        // Checked: clean ✓ only (no outer box) so dense form rows stay readable
        if (ann.checked) {
          const boxW = (ann.widthPct || 0.016) * width;
          const boxH = (ann.heightPct || 0.016) * height;
          const x = ann.xPct * width;
          const y = height - ann.yPct * height - boxH;
          const thickness = Math.max(0.7, Math.min(boxW, boxH) * 0.12);
          page.drawLine({
            start: { x: x + boxW * 0.18, y: y + boxH * 0.48 },
            end: { x: x + boxW * 0.4, y: y + boxH * 0.22 },
            thickness,
            color: rgb(0.05, 0.05, 0.05),
            lineCap: 1,
          });
          page.drawLine({
            start: { x: x + boxW * 0.38, y: y + boxH * 0.22 },
            end: { x: x + boxW * 0.82, y: y + boxH * 0.78 },
            thickness,
            color: rgb(0.05, 0.05, 0.05),
            lineCap: 1,
          });
        }
      } else if (ann.type === "draw" && ann.points?.length > 1) {
        const c = hexToRgb(ann.color || "#111111");
        const sw = Math.max(0.6, (ann.strokeWidthPct || 0.004) * width);
        const op = ann.opacity ?? 1;
        for (let i = 1; i < ann.points.length; i++) {
          const p0 = ann.points[i - 1];
          const p1 = ann.points[i];
          page.drawLine({
            start: {
              x: p0.xPct * width,
              y: height - p0.yPct * height,
            },
            end: {
              x: p1.xPct * width,
              y: height - p1.yPct * height,
            },
            thickness: sw,
            color: rgb(c.r, c.g, c.b),
            opacity: op,
            lineCap: 1,
          });
        }
      } else if (
        (ann.type === "signature" || ann.type === "photo") &&
        ann.imageDataUrl
      ) {
        const bytes = await dataUrlToUint8Array(ann.imageDataUrl);
        const isPng = ann.imageDataUrl.startsWith("data:image/png");
        const image = isPng
          ? await pdfDoc.embedPng(bytes)
          : await pdfDoc.embedJpg(bytes);
        const w = (ann.widthPct || 0.2) * width;
        const h = (ann.heightPct || 0.1) * height;
        page.drawImage(image, {
          x: ann.xPct * width,
          y: height - ann.yPct * height - h,
          width: w,
          height: h,
        });
      }
    }

    return pdfDoc.save();
  };

  const pdfBytesToImageBlobs = async (pdfBytes) => {
    const uri = URL.createObjectURL(
      new Blob([pdfBytes], { type: "application/pdf" }),
    );
    try {
      const doc = await pdfjs.getDocument(uri).promise;
      const blobs = [];
      for (let i = 1; i <= doc.numPages; i++) {
        const page = await doc.getPage(i);
        const viewport = page.getViewport({ scale: 1.5 });
        const canvas = document.createElement("canvas");
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        await page
          .render({ canvasContext: canvas.getContext("2d"), viewport })
          .promise;
        const blob = await new Promise((resolve) =>
          canvas.toBlob(resolve, "image/jpeg", 0.92),
        );
        if (blob) blobs.push(blob);
      }
      return blobs;
    } finally {
      URL.revokeObjectURL(uri);
    }
  };

  const ensureTemplateBytes = async () => {
    if (templateBytesRef.current) return templateBytesRef.current;
    if (bytesReadyPromiseRef.current) {
      const ready = await bytesReadyPromiseRef.current;
      if (ready) {
        const uint8 =
          ready instanceof Uint8Array ? ready : new Uint8Array(ready);
        const ab = uint8.buffer.slice(
          uint8.byteOffset,
          uint8.byteOffset + uint8.byteLength,
        );
        templateBytesRef.current = ab;
        setTemplateBytes(ab);
        return ab;
      }
    }
    return templateBytesRef.current || null;
  };

  const handleSaveAndUpload = async () => {
    if (!preauthDocumentId) {
      toast.error(
        "Preauth Template document is not mapped on this case yet. Map it first, then fill & upload.",
      );
      return;
    }
    const hasContent = annotations.some(
      (a) =>
        (a.type === "text" && a.text?.trim()) ||
        a.type === "draw" ||
        a.type === "highlight" ||
        a.type === "whiteout" ||
        a.type === "checkbox" ||
        a.type === "signature" ||
        a.type === "photo",
    );
    if (!hasContent) {
      toast.error(
        "Add text, checkbox, drawing, signature, or photo before uploading",
      );
      return;
    }

    setSaving(true);
    try {
      const bytes = await ensureTemplateBytes();
      if (!bytes) {
        toast.error(
          "Still preparing the previous form for save. Please try again in a moment.",
        );
        return;
      }
      const filledBytes = await buildFilledPdfBytesWith(bytes);
      const imageBlobs = await pdfBytesToImageBlobs(filledBytes);
      if (!imageBlobs.length) {
        toast.error("Could not prepare pages for upload");
        return;
      }

      const formData = new FormData();
      imageBlobs.forEach((blob, index) => {
        formData.append("images", blob, `Preauth_Template_${index + 1}.jpeg`);
      });

      const uploadRes = await AuthServices.uploadPdfToImage(formData, () => {});
      const urls = uploadRes?.data?.urls;
      if (!urls?.length) {
        toast.error("Upload failed. Please try again.");
        return;
      }

      const postData = urls.map((documentUrl, index) => ({
        documentUrl,
        sequence: index + 1,
        urlId: "",
      }));

      await CaseServices.saveDocumentUrl(preauthDocumentId, postData, true);
      // Blank-template fills: restore annotations next open (fully editable/deletable).
      // Raster (image) fills: marks are baked into the new pages — keep raster reopen.
      if (loadedAsRasterRef.current) {
        persistFillSession(preauthDocumentId, [], true);
      } else {
        persistFillSession(preauthDocumentId, annotations, false);
      }
      toast.success("Filled template uploaded to Preauth Template");
      onUploaded?.();
      onClose?.();
    } catch (error) {
      handleApiErrorResponses(error);
    } finally {
      setSaving(false);
    }
  };

  const toolBtnClass = (t) =>
    `flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold border border-solid cursor-pointer whitespace-nowrap ${
      tool === t
        ? "bg-[#017C57] text-white border-[#017C57]"
        : "bg-white text-[#017C57] border-[#017C57]"
    }`;

  const pageCursorClass =
    tool === TOOLS.DRAW ||
    tool === TOOLS.HIGHLIGHT ||
    tool === TOOLS.WHITEOUT ||
    tool === TOOLS.CHECKBOX
      ? "cursor-crosshair"
      : tool === TOOLS.TEXT
        ? "cursor-text"
        : "cursor-default";

  const renderPageOverlays = (index) => (
    <>
      {/* draw strokes as SVG overlay */}
      <svg
        className="absolute inset-0 w-full h-full pointer-events-none"
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
      >
        {annotations
          .filter(
            (a) => a.pageIndex === index && a.type === "draw",
          )
          .map((ann) => (
            <polyline
              key={ann.id}
              fill="none"
              stroke={ann.color || "#111"}
              strokeWidth={Math.max(
                0.15,
                (ann.strokeWidthPct || 0.004) * 100,
              )}
              strokeLinecap="round"
              strokeLinejoin="round"
              points={ann.points
                .map((p) => `${p.xPct * 100},${p.yPct * 100}`)
                .join(" ")}
              opacity={ann.opacity ?? 1}
            />
          ))}
      </svg>

      {annotations
        .filter(
          (a) =>
            a.pageIndex === index &&
            a.type !== "draw",
        )
        .map((ann) => {
          const isSelected = selectedId === ann.id;
          const isText = ann.type === "text";
          const isCheckbox = ann.type === "checkbox";
          return (
          <div
            key={ann.id}
            data-annotation
            className={`absolute ${
              isText
                ? isSelected
                  ? "z-10 ring-0 outline-none"
                  : "ring-0 outline-none"
                : isSelected
                  ? "ring-2 ring-[#017C57] z-10"
                  : isCheckbox
                    ? "ring-0"
                    : "ring-1 ring-transparent hover:ring-[#017C57]/50"
            }`}
            style={{
              left: `${ann.xPct * 100}%`,
              top: `${ann.yPct * 100}%`,
              width: `${(ann.widthPct || 0.2) * 100}%`,
              minHeight: isText
                ? undefined
                : `${(ann.heightPct || 0.04) * 100}%`,
              height:
                ann.type === "highlight" ||
                ann.type === "whiteout" ||
                ann.type === "signature" ||
                ann.type === "photo" ||
                isCheckbox
                  ? `${(ann.heightPct || 0.08) * 100}%`
                  : isText
                    ? `${(ann.heightPct || 0.02) * 100}%`
                    : undefined,
              backgroundColor:
                ann.type === "whiteout"
                  ? "#FFFFFF"
                  : ann.type === "highlight"
                    ? `${ann.color || "#FFE566"}99`
                    : "transparent",
              boxShadow: "none",
              border: "none",
              overflow: isText ? "visible" : undefined,
              cursor: isText
                ? tool === TOOLS.TEXT
                  ? "text"
                  : "text"
                : tool === TOOLS.SELECT ||
                    tool === TOOLS.TEXT ||
                    tool === TOOLS.CHECKBOX
                  ? isCheckbox
                    ? "pointer"
                    : "move"
                  : "inherit",
            }}
            onPointerDown={(e) => {
              if (isCheckbox) {
                e.stopPropagation();
                e.preventDefault();
                setSelectedId(ann.id);
                const pageEl = e.currentTarget.closest("[data-page]");
                if (!pageEl) {
                  toggleCheckbox(ann.id);
                  return;
                }
                const rect = pageEl.getBoundingClientRect();
                const startX = e.clientX;
                const startY = e.clientY;
                const origX = ann.xPct;
                const origY = ann.yPct;
                let moved = false;
                const snapshot = annotations;

                const onMove = (ev) => {
                  const dx = (ev.clientX - startX) / rect.width;
                  const dy = (ev.clientY - startY) / rect.height;
                  if (
                    !moved &&
                    (Math.abs(dx) > 0.004 || Math.abs(dy) > 0.004)
                  ) {
                    moved = true;
                  }
                  if (!moved) return;
                  setAnnotations((prev) =>
                    prev.map((a) =>
                      a.id === ann.id
                        ? {
                            ...a,
                            xPct: clamp(origX + dx, 0, 0.98),
                            yPct: clamp(origY + dy, 0, 0.98),
                          }
                        : a,
                    ),
                  );
                };
                const onUp = () => {
                  window.removeEventListener(
                    "pointermove",
                    onMove,
                  );
                  window.removeEventListener("pointerup", onUp);
                  if (moved) {
                    setUndoStack((u) => [
                      ...u.slice(-49),
                      snapshot,
                    ]);
                    setRedoStack([]);
                  } else {
                    toggleCheckbox(ann.id);
                  }
                };
                window.addEventListener("pointermove", onMove);
                window.addEventListener("pointerup", onUp);
                return;
              }
              if (
                tool === TOOLS.SELECT ||
                tool === TOOLS.TEXT ||
                tool === TOOLS.CHECKBOX
              ) {
                startDrag(e, ann.id);
              } else {
                e.stopPropagation();
                setSelectedId(ann.id);
              }
            }}
            onClick={(e) => {
              e.stopPropagation();
              if (isCheckbox) return;
              setSelectedId(ann.id);
            }}
            onDoubleClick={(e) => {
              if (!isText) return;
              e.stopPropagation();
              setSelectedId(ann.id);
            }}
          >
            {isText ? (
              isSelected ? (
                <textarea
                  autoFocus
                  value={ann.text}
                  placeholder=""
                  wrap="off"
                  rows={1}
                  className="w-full h-full min-h-0 border-0 outline-none ring-0 shadow-none bg-transparent text-black resize-none p-0 overflow-visible caret-black"
                  style={{
                    fontFamily: getFontOption(ann.fontFamily).css,
                    fontSize:
                      ann.fontSizePct != null
                        ? ann.fontSizePct * pageWidth
                        : ann.fontSize || 10,
                    letterSpacing: `${ann.letterSpacing ?? 0}px`,
                    lineHeight: 1.35,
                    height: "100%",
                    border: "none",
                    outline: "none",
                    boxShadow: "none",
                    background: "transparent",
                    whiteSpace: "pre",
                    overflowWrap: "normal",
                    wordBreak: "keep-all",
                  }}
                  onChange={(e) => {
                    const text = e.target.value;
                    setAnnotations((prev) =>
                      prev.map((a) => {
                        if (a.id !== ann.id) return a;
                        if (a.autoSize === false) {
                          return { ...a, text };
                        }
                        return {
                          ...a,
                          text,
                          widthPct: estimateTextWidthPct(
                            text,
                            a.fontSize || 10,
                            pageWidth,
                            a.letterSpacing ?? 0,
                            getFontOption(a.fontFamily).css,
                          ),
                          heightPct: estimateTextHeightPct(
                            text,
                            a.fontSize || 10,
                            pageWidth,
                          ),
                        };
                      }),
                    );
                  }}
                  onBlur={(e) => {
                    // Keep selection when using header font controls or Delete
                    if (
                      e.relatedTarget?.closest?.(
                        "[data-text-style-bar], [data-keep-text-selection]",
                      )
                    ) {
                      return;
                    }
                    const isEmpty = !String(
                      ann.text || "",
                    ).trim();
                    if (isEmpty) {
                      pushHistory(
                        annotations.filter(
                          (a) => a.id !== ann.id,
                        ),
                      );
                      setSelectedId(null);
                      return;
                    }
                    setUndoStack((u) => {
                      if (u[u.length - 1] === annotations)
                        return u;
                      return [...u.slice(-49), annotations];
                    });
                    setRedoStack([]);
                    setSelectedId(null);
                  }}
                  onClick={(e) => e.stopPropagation()}
                  onPointerDown={(e) => e.stopPropagation()}
                />
              ) : (
                <div
                  className="w-full text-black p-0 overflow-visible"
                  style={{
                    fontFamily: getFontOption(ann.fontFamily).css,
                    fontSize:
                      ann.fontSizePct != null
                        ? ann.fontSizePct * pageWidth
                        : ann.fontSize || 10,
                    letterSpacing: `${ann.letterSpacing ?? 0}px`,
                    lineHeight: 1.35,
                    border: "none",
                    background: "transparent",
                    whiteSpace: "pre",
                    overflowWrap: "normal",
                    wordBreak: "keep-all",
                  }}
                >
                  {ann.text}
                </div>
              )
            ) : isCheckbox ? (
              <div
                className="w-full h-full flex items-center justify-center select-none bg-transparent"
                title={
                  ann.checked
                    ? "Checked — click to uncheck"
                    : "Unchecked — click to check"
                }
              >
                {ann.checked ? (
                  <span
                    aria-hidden
                    className="leading-none text-black select-none"
                    style={{
                      fontSize: `${Math.max(
                        9,
                        Math.round(
                          (ann.widthPct || checkboxSizePct) * pageWidth * 0.85,
                        ),
                      )}px`,
                      fontWeight: 500,
                    }}
                  >
                    ✓
                  </span>
                ) : isSelected ? (
                  <div
                    className="w-[55%] h-[55%] border border-dashed border-[#017C57]/50 rounded-[2px]"
                    aria-hidden
                  />
                ) : null}
              </div>
            ) : ann.type === "signature" ||
              ann.type === "photo" ? (
              <img
                src={ann.imageDataUrl}
                alt={ann.type}
                className="w-full h-full object-contain pointer-events-none"
                draggable={false}
              />
            ) : null}

            {/* No resize handle on text — keeps form readable like Word */}
            {isSelected &&
              !isText &&
              ann.type !== "draw" && (
              <div
                className="absolute -right-1.5 -bottom-1.5 w-3.5 h-3.5 rounded-full bg-[#017C57] border-2 border-white cursor-se-resize"
                onPointerDown={(e) => startResize(e, ann.id)}
                title="Resize"
              />
            )}
          </div>
          );
        })}
    </>
  );

  const renderFillPage = (index, background) => (
    <div
      key={`page-${index}`}
      data-page={index}
      ref={(el) => {
        pageRefs.current[index] = el;
      }}
      className={`relative mb-4 bg-white shadow ${pageCursorClass}`}
      style={{ width: pageWidth, touchAction: "none" }}
      onPointerDown={(e) => handlePagePointerDown(e, index)}
    >
      {background}
      {renderPageOverlays(index)}
    </div>
  );

  const selectedAnn = annotations.find((a) => a.id === selectedId);

  return (
    <div className="fixed inset-0 z-[1300] flex items-center justify-center bg-black/50 p-2 sm:p-3">
      <div className="bg-white w-full max-w-[98vw] h-[96vh] rounded-xl flex flex-col shadow-xl overflow-hidden">
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-solid border-[#E7E7E7] shrink-0">
          <div>
            <div className="font-semibold text-base">Fill TPA Template</div>
            <div className="text-xs text-[#666]">
              View case documents on the left while filling the form on the
              right.
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold border border-solid border-[#017C57] text-[#017C57] bg-white cursor-pointer"
              onClick={() => setShowDocsPanel((v) => !v)}
              title={showDocsPanel ? "Hide documents" : "Show documents"}
            >
              <MenuOpenIcon sx={{ fontSize: 18 }} />
              {showDocsPanel ? "Hide docs" : "Show docs"}
            </button>
            <button
              type="button"
              className="p-1 rounded-full hover:bg-gray-100 cursor-pointer border-0 bg-transparent"
              onClick={onClose}
              disabled={saving}
            >
              <CloseIcon />
            </button>
          </div>
        </div>

        <div className="flex flex-1 min-h-0 w-full">
          {/* Left 40%: case documents reference viewer */}
          {showDocsPanel && (
            <div className="w-[40%] min-w-0 border-r border-solid border-[#E7E7E7] flex flex-col min-h-0 bg-white">
              <div className="px-2 py-1 border-b border-solid border-[#E7E7E7] shrink-0 flex items-center justify-between gap-2 bg-[#F8FAF9]">
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-semibold text-[#017C57]">
                    Case documents
                  </div>
                  {activeRefDoc && (
                    <div
                      className="text-[11px] text-[#333] font-medium truncate"
                      title={activeRefDoc.documentName}
                    >
                      {activeRefDoc.documentName}
                      {activeRefDoc.status ? ` · ${activeRefDoc.status}` : ""}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    type="button"
                    className="p-0.5 rounded border border-solid border-[#CCC] bg-white cursor-pointer disabled:opacity-40"
                    disabled={refZoom <= 0.6}
                    onClick={() =>
                      setRefZoom((z) => clamp(Number((z - 0.15).toFixed(2)), 0.6, 2.5))
                    }
                    title="Zoom out"
                  >
                    <ZoomOutIcon sx={{ fontSize: 16 }} />
                  </button>
                  <span className="text-[10px] font-semibold text-[#444] min-w-[34px] text-center">
                    {Math.round(refZoom * 100)}%
                  </span>
                  <button
                    type="button"
                    className="p-0.5 rounded border border-solid border-[#CCC] bg-white cursor-pointer disabled:opacity-40"
                    disabled={refZoom >= 2.5}
                    onClick={() =>
                      setRefZoom((z) => clamp(Number((z + 0.15).toFixed(2)), 0.6, 2.5))
                    }
                    title="Zoom in"
                  >
                    <ZoomInIcon sx={{ fontSize: 16 }} />
                  </button>
                  <span className="w-px h-4 bg-[#DDD] mx-0.5" />
                  <button
                    type="button"
                    className="p-0.5 rounded border border-solid border-[#CCC] bg-white cursor-pointer disabled:opacity-40"
                    disabled={refPageIdx <= 0}
                    onClick={() => setRefPageIdx((i) => Math.max(0, i - 1))}
                  >
                    <ChevronLeftIcon sx={{ fontSize: 18 }} />
                  </button>
                  <span className="text-[11px] font-semibold text-[#444] min-w-[40px] text-center">
                    {refTotalPages
                      ? `${Math.min(refPageIdx + 1, refTotalPages)}/${refTotalPages}`
                      : "—"}
                  </span>
                  <button
                    type="button"
                    className="p-0.5 rounded border border-solid border-[#CCC] bg-white cursor-pointer disabled:opacity-40"
                    disabled={refPageIdx >= refTotalPages - 1}
                    onClick={() =>
                      setRefPageIdx((i) =>
                        Math.min(refTotalPages - 1, i + 1),
                      )
                    }
                  >
                    <ChevronRightIcon sx={{ fontSize: 18 }} />
                  </button>
                </div>
              </div>

              <div className="shrink-0 border-b border-solid border-[#E7E7E7] px-1.5 py-1 overflow-x-auto bg-[#F8FAF9]">
                {viewableDocs.length === 0 ? (
                  <div className="text-xs text-[#888] py-1 px-1">
                    No other documents uploaded yet.
                  </div>
                ) : (
                  <div className="flex gap-1 min-w-max">
                    {viewableDocs.map((doc) => {
                      const pages = getDocPages(doc);
                      const selected = activeRefDoc?.id === doc.id;
                      return (
                        <button
                          key={doc.id}
                          type="button"
                          onClick={() => {
                            setRefDocId(doc.id);
                            setRefPageIdx(0);
                            setRefPdfPageCount(0);
                            setRefZoom(1);
                          }}
                          className={`flex items-center gap-1 max-w-[160px] px-2 py-0.5 rounded-full border border-solid cursor-pointer text-left ${
                            selected
                              ? "bg-[#017C57] text-white border-[#017C57]"
                              : "bg-white text-[#222] border-[#DDD] hover:border-[#017C57]"
                          }`}
                          title={doc.documentName || "Document"}
                        >
                          <DescriptionOutlinedIcon sx={{ fontSize: 13 }} />
                          <span className="text-[10px] font-semibold truncate">
                            {doc.documentName || "Document"}
                          </span>
                          <span
                            className={`text-[9px] shrink-0 ${
                              selected ? "text-white/80" : "text-[#888]"
                            }`}
                          >
                            {pages.length}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Edge-to-edge preview (no browser PDF sidebar / empty chrome) */}
              <div className="flex-1 min-h-0 overflow-auto bg-[#F0F0F0]">
                {!activeRefPage ? (
                  <div className="h-full flex items-center justify-center text-xs text-[#888] text-center px-3">
                    Select a document to preview
                  </div>
                ) : activeRefIsPdf ? (
                  <div className="w-full flex justify-center p-0">
                    <Document
                      key={activeRefPage.documentUrl}
                      file={activeRefPage.documentUrl}
                      onLoadSuccess={({ numPages: n }) => {
                        setRefPdfPageCount(n);
                        setRefPageIdx((i) => Math.min(i, Math.max(0, n - 1)));
                      }}
                      loading={
                        <div className="py-10">
                          <CircularProgress
                            size={24}
                            sx={{ color: "#017C57" }}
                          />
                        </div>
                      }
                      error={
                        <div className="text-xs text-[#CF3636] p-3">
                          Could not load PDF preview
                        </div>
                      }
                    >
                      <Page
                        pageNumber={
                          activeRefIsPdfFile
                            ? Math.min(refPageIdx + 1, refTotalPages || 1)
                            : 1
                        }
                        width={Math.max(
                          200,
                          Math.floor(
                            ((typeof window !== "undefined"
                              ? window.innerWidth * 0.4
                              : 480) -
                              4) *
                              refZoom,
                          ),
                        )}
                        renderTextLayer={false}
                        renderAnnotationLayer={false}
                        className="!m-0"
                      />
                    </Document>
                  </div>
                ) : (
                  <img
                    src={activeRefPage.documentUrl}
                    alt={activeRefDoc?.documentName || "Reference document"}
                    className="h-auto block bg-white"
                    style={{
                      width: `${Math.round(refZoom * 100)}%`,
                      maxWidth: "none",
                    }}
                  />
                )}
              </div>
            </div>
          )}

          {/* Right 60%: fill template editor */}
          <div
            className={`flex flex-col min-w-0 min-h-0 ${
              showDocsPanel ? "w-[60%]" : "w-full"
            }`}
          >
        <div className="flex flex-wrap items-center gap-2 px-2 py-2 border-b border-solid border-[#E7E7E7] bg-[#FAFAFA] shrink-0">
          <button
            type="button"
            className={toolBtnClass(TOOLS.SELECT)}
            onClick={() => setTool(TOOLS.SELECT)}
            title="Select / move"
          >
            <PanToolAltIcon sx={{ fontSize: 18 }} /> Select
          </button>
          <button
            type="button"
            className={toolBtnClass(TOOLS.TEXT)}
            onClick={() => setTool(TOOLS.TEXT)}
            title="Click on page to add text"
          >
            <TextFieldsIcon sx={{ fontSize: 18 }} /> Text
          </button>
          <button
            type="button"
            className={toolBtnClass(TOOLS.DRAW)}
            onClick={() => setTool(TOOLS.DRAW)}
            title="Draw freehand"
          >
            <DrawIcon sx={{ fontSize: 18 }} /> Draw
          </button>
          <button
            type="button"
            className={toolBtnClass(TOOLS.HIGHLIGHT)}
            onClick={() => setTool(TOOLS.HIGHLIGHT)}
            title="Drag to highlight"
          >
            <HighlightIcon sx={{ fontSize: 18 }} /> Highlight
          </button>
          <button
            type="button"
            className={toolBtnClass(TOOLS.WHITEOUT)}
            onClick={() => setTool(TOOLS.WHITEOUT)}
            title="Drag to cover mistakes on a previously saved form image"
          >
            Whiteout
          </button>
          <button
            type="button"
            className={toolBtnClass(TOOLS.CHECKBOX)}
            onClick={() => setTool(TOOLS.CHECKBOX)}
            title="Click on form to place checkbox; click again to check/uncheck"
          >
            <CheckBoxOutlinedIcon sx={{ fontSize: 18 }} /> Checkbox
          </button>
          <button
            type="button"
            className={toolBtnClass(TOOLS.SIGNATURE)}
            onClick={() => {
              setTool(TOOLS.SIGNATURE);
              setSignatureOpen(true);
            }}
          >
            <DrawIcon sx={{ fontSize: 18 }} /> Signature
          </button>
          <button
            type="button"
            className={toolBtnClass(TOOLS.PHOTO)}
            onClick={() => photoInputRef.current?.click()}
          >
            <PhotoCameraIcon sx={{ fontSize: 18 }} /> Photo
          </button>

          <span className="w-px h-6 bg-[#DDD] mx-1" />

          <button
            type="button"
            className="p-1.5 rounded-full border border-solid border-[#CCC] bg-white cursor-pointer disabled:opacity-40"
            onClick={undo}
            disabled={!undoStack.length}
            title="Undo (Ctrl+Z)"
          >
            <UndoIcon sx={{ fontSize: 18 }} />
          </button>
          <button
            type="button"
            className="p-1.5 rounded-full border border-solid border-[#CCC] bg-white cursor-pointer disabled:opacity-40"
            onClick={redo}
            disabled={!redoStack.length}
            title="Redo (Ctrl+Y)"
          >
            <RedoIcon sx={{ fontSize: 18 }} />
          </button>
          <button
            type="button"
            data-keep-text-selection
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-full text-sm font-semibold border border-solid border-[#CF3636] text-[#CF3636] bg-white cursor-pointer disabled:opacity-40"
            onMouseDown={(e) => e.preventDefault()}
            onClick={deleteSelected}
            disabled={
              !selectedId &&
              !annotations.some(
                (a) => a.type === "text" && !String(a.text || "").trim(),
              )
            }
            title="Delete selected item, or clear empty text boxes"
          >
            <DeleteOutlineIcon sx={{ fontSize: 18 }} /> Delete
          </button>

          <span className="w-px h-6 bg-[#DDD] mx-1" />

          <button
            type="button"
            className="p-1.5 rounded-full border border-solid border-[#CCC] bg-white cursor-pointer"
            onClick={() => setZoom((z) => clamp(z - 0.15, 0.6, 1.8))}
          >
            <ZoomOutIcon sx={{ fontSize: 18 }} />
          </button>
          <span className="text-xs font-semibold w-10 text-center">
            {Math.round(zoom * 100)}%
          </span>
          <button
            type="button"
            className="p-1.5 rounded-full border border-solid border-[#CCC] bg-white cursor-pointer"
            onClick={() => setZoom((z) => clamp(z + 0.15, 0.6, 1.8))}
          >
            <ZoomInIcon sx={{ fontSize: 18 }} />
          </button>

          {tool === TOOLS.DRAW && (
            <div className="flex flex-wrap items-center gap-2 text-xs font-semibold text-[#444]">
              <input
                type="color"
                value={drawColor}
                onChange={(e) => setDrawColor(e.target.value)}
                title="Pencil color"
                className="w-8 h-8 p-0 border border-solid border-[#CCC] rounded cursor-pointer"
              />
              <div className="flex items-center gap-1">
                <span title="Quick shades">Shade</span>
                {[
                  { label: "Black", value: "#111111" },
                  { label: "Dark", value: "#555555" },
                  { label: "Gray", value: "#999999" },
                  { label: "Light", value: "#CCCCCC" },
                ].map((s) => (
                  <button
                    key={s.value}
                    type="button"
                    title={s.label}
                    className={`w-5 h-5 rounded-full border border-solid cursor-pointer ${
                      drawColor.toLowerCase() === s.value
                        ? "border-[#017C57] ring-2 ring-[#017C57]/40"
                        : "border-[#AAA]"
                    }`}
                    style={{ backgroundColor: s.value }}
                    onClick={() => setDrawColor(s.value)}
                  />
                ))}
              </div>
              <label className="flex items-center gap-1" title="Pencil thickness">
                Width
                <input
                  type="range"
                  min={1}
                  max={8}
                  step={1}
                  value={drawStrokeWidth}
                  onChange={(e) => setDrawStrokeWidth(Number(e.target.value))}
                  className="w-20 cursor-pointer"
                />
                <span className="w-4">{drawStrokeWidth}</span>
              </label>
              <label
                className="flex items-center gap-1"
                title="Lighten pencil (opacity)"
              >
                Lighten
                <input
                  type="range"
                  min={0.25}
                  max={1}
                  step={0.05}
                  value={drawOpacity}
                  onChange={(e) => setDrawOpacity(Number(e.target.value))}
                  className="w-20 cursor-pointer"
                />
                <span className="w-8">{Math.round(drawOpacity * 100)}%</span>
              </label>
            </div>
          )}
          {tool === TOOLS.HIGHLIGHT && (
            <input
              type="color"
              value={highlightColor}
              onChange={(e) => setHighlightColor(e.target.value)}
              title="Highlight color"
              className="w-8 h-8 p-0 border border-solid border-[#CCC] rounded cursor-pointer"
            />
          )}
          {(tool === TOOLS.CHECKBOX || selectedAnn?.type === "checkbox") && (
            <div className="flex items-center gap-2 text-xs font-semibold text-[#444]">
              <span>Box size</span>
              <button
                type="button"
                className="px-2 py-1 rounded-full border border-solid border-[#017C57] text-[#017C57] bg-white cursor-pointer"
                title="Make checkbox smaller"
                onClick={() => {
                  const next = clamp(
                    (selectedAnn?.type === "checkbox"
                      ? selectedAnn.widthPct
                      : checkboxSizePct) - 0.006,
                    0.012,
                    0.08,
                  );
                  setCheckboxSizePct(next);
                  if (selectedAnn?.type === "checkbox") {
                    setAnnotations((prev) =>
                      prev.map((a) =>
                        a.id === selectedId
                          ? { ...a, widthPct: next, heightPct: next }
                          : a,
                      ),
                    );
                  }
                }}
              >
                −
              </button>
              <input
                type="range"
                min={0.012}
                max={0.08}
                step={0.002}
                value={
                  selectedAnn?.type === "checkbox"
                    ? selectedAnn.widthPct || checkboxSizePct
                    : checkboxSizePct
                }
                onChange={(e) => {
                  const next = Number(e.target.value);
                  setCheckboxSizePct(next);
                  if (selectedAnn?.type === "checkbox") {
                    setAnnotations((prev) =>
                      prev.map((a) =>
                        a.id === selectedId
                          ? { ...a, widthPct: next, heightPct: next }
                          : a,
                      ),
                    );
                  }
                }}
                className="w-24 cursor-pointer"
                title="Checkbox size"
              />
              <button
                type="button"
                className="px-2 py-1 rounded-full border border-solid border-[#017C57] text-[#017C57] bg-white cursor-pointer"
                title="Make checkbox larger"
                onClick={() => {
                  const next = clamp(
                    (selectedAnn?.type === "checkbox"
                      ? selectedAnn.widthPct
                      : checkboxSizePct) + 0.006,
                    0.012,
                    0.08,
                  );
                  setCheckboxSizePct(next);
                  if (selectedAnn?.type === "checkbox") {
                    setAnnotations((prev) =>
                      prev.map((a) =>
                        a.id === selectedId
                          ? { ...a, widthPct: next, heightPct: next }
                          : a,
                      ),
                    );
                  }
                }}
              >
                +
              </button>
            </div>
          )}
          {(tool === TOOLS.TEXT || selectedAnn?.type === "text") && (
            <div
              data-text-style-bar
              className="flex flex-wrap items-center gap-2 text-xs font-semibold text-[#444]"
            >
              <label className="flex items-center gap-1">
                Font
                <select
                  className="border border-solid border-[#CCC] rounded px-1.5 py-1 bg-white cursor-pointer min-w-[110px]"
                  value={
                    selectedAnn?.type === "text"
                      ? selectedAnn.fontFamily || defaultFontFamily
                      : defaultFontFamily
                  }
                  onChange={(e) =>
                    applyTextStyle({ fontFamily: e.target.value })
                  }
                  onMouseDown={(e) => e.preventDefault()}
                >
                  {FONT_OPTIONS.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex items-center gap-1">
                Size
                <button
                  type="button"
                  className="px-2 py-0.5 rounded border border-solid border-[#017C57] text-[#017C57] bg-white cursor-pointer"
                  onClick={() =>
                    applyTextStyle({
                      fontSize: clamp(
                        (selectedAnn?.type === "text"
                          ? selectedAnn.fontSize || defaultFontSize
                          : defaultFontSize) - 1,
                        8,
                        36,
                      ),
                    })
                  }
                >
                  −
                </button>
                <input
                  type="number"
                  min={8}
                  max={36}
                  value={
                    selectedAnn?.type === "text"
                      ? selectedAnn.fontSize || defaultFontSize
                      : defaultFontSize
                  }
                  className="w-14 border border-solid border-[#CCC] rounded px-1 py-0.5"
                  onChange={(e) =>
                    applyTextStyle({
                      fontSize: clamp(Number(e.target.value) || 13, 8, 36),
                    })
                  }
                />
                <button
                  type="button"
                  className="px-2 py-0.5 rounded border border-solid border-[#017C57] text-[#017C57] bg-white cursor-pointer"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() =>
                    applyTextStyle({
                      fontSize: clamp(
                        (selectedAnn?.type === "text"
                          ? selectedAnn.fontSize || defaultFontSize
                          : defaultFontSize) + 1,
                        8,
                        36,
                      ),
                    })
                  }
                >
                  +
                </button>
              </label>
              <label className="flex items-center gap-1" title="Letter spacing (px)">
                Spacing
                <button
                  type="button"
                  className="px-2 py-0.5 rounded border border-solid border-[#017C57] text-[#017C57] bg-white cursor-pointer"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() =>
                    applyTextStyle({
                      letterSpacing: clamp(
                        (selectedAnn?.type === "text"
                          ? selectedAnn.letterSpacing ?? defaultLetterSpacing
                          : defaultLetterSpacing) - 0.5,
                        -2,
                        20,
                      ),
                    })
                  }
                >
                  −
                </button>
                <input
                  type="number"
                  min={-2}
                  max={20}
                  step={0.5}
                  value={
                    selectedAnn?.type === "text"
                      ? selectedAnn.letterSpacing ?? defaultLetterSpacing
                      : defaultLetterSpacing
                  }
                  className="w-14 border border-solid border-[#CCC] rounded px-1 py-0.5"
                  onMouseDown={(e) => e.preventDefault()}
                  onChange={(e) =>
                    applyTextStyle({
                      letterSpacing: clamp(
                        Number(e.target.value) || 0,
                        -2,
                        20,
                      ),
                    })
                  }
                />
                <button
                  type="button"
                  className="px-2 py-0.5 rounded border border-solid border-[#017C57] text-[#017C57] bg-white cursor-pointer"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() =>
                    applyTextStyle({
                      letterSpacing: clamp(
                        (selectedAnn?.type === "text"
                          ? selectedAnn.letterSpacing ?? defaultLetterSpacing
                          : defaultLetterSpacing) + 0.5,
                        -2,
                        20,
                      ),
                    })
                  }
                >
                  +
                </button>
              </label>
            </div>
          )}

          <input
            ref={photoInputRef}
            type="file"
            accept=".png,.jpg,.jpeg,image/png,image/jpeg"
            className="hidden"
            onChange={handlePhotoPick}
          />
        </div>

        <div
          ref={scrollContainerRef}
          className="flex-1 overflow-auto bg-[#EDEDED] px-2 py-2 min-h-0"
        >
          {numPages > 1 && !loading && (
            <div className="sticky top-0 z-30 flex justify-center mb-2 pointer-events-none">
              <span className="text-xs font-semibold bg-white/95 text-[#017C57] px-3 py-1 rounded-full border border-solid border-[#017C57] shadow-sm">
                Page {currentPageIndex + 1} of {numPages}
              </span>
            </div>
          )}
          {loading ? (
            <div className="flex justify-center items-center min-h-[320px]">
              <CircularProgress sx={{ color: "#017C57" }} />
            </div>
          ) : imageTemplatePages.length > 0 ? (
            <div className="flex flex-col items-center gap-4">
              {imageTemplatePages.map((url, index) =>
                renderFillPage(
                  index,
                  <img
                    src={url}
                    alt={`Filled page ${index + 1}`}
                    className="block w-full h-auto select-none pointer-events-none"
                    draggable={false}
                  />,
                ),
              )}
            </div>
          ) : templateUrl ? (
            <div className="flex flex-col items-center gap-4">
              <Document
                file={templateUrl}
                onLoadSuccess={({ numPages: n }) => setNumPages(n)}
                loading={
                  <CircularProgress size={28} sx={{ color: "#017C57" }} />
                }
                error={
                  <div className="text-sm text-[#CF3636]">
                    Failed to render PDF template
                  </div>
                }
              >
                {Array.from({ length: numPages }, (_, index) =>
                  renderFillPage(
                    index,
                    <Page
                      pageNumber={index + 1}
                      width={pageWidth}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                    />,
                  ),
                )}
              </Document>
            </div>
          ) : (
            <div className="text-center text-sm text-[#666] py-16">
              No template loaded
            </div>
          )}
        </div>

        <div className="flex justify-between items-center gap-3 px-4 py-3 border-t border-solid border-[#E7E7E7] shrink-0">
          <div className="text-xs text-[#777] hidden lg:block">
            Tip: Use left panel to check case docs while filling on the right.
          </div>
          <div className="flex gap-3 ml-auto">
            <button
              type="button"
              className="px-5 py-2 rounded-full text-sm font-semibold border border-solid border-[#017C57] text-[#017C57] bg-white cursor-pointer disabled:opacity-50"
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="button"
              className="px-5 py-2 rounded-full text-sm font-semibold border border-solid border-[#017C57] bg-[#017C57] text-white cursor-pointer disabled:opacity-50 flex items-center gap-2"
              onClick={handleSaveAndUpload}
              disabled={saving || loading || (!templateBytes && imageTemplatePages.length === 0)}
            >
              {saving && (
                <CircularProgress size={16} sx={{ color: "#FFFFFF" }} />
              )}
              {saving ? "Uploading..." : "Save & Upload"}
            </button>
          </div>
        </div>
          </div>
        </div>
      </div>

      {signatureOpen && (
        <div className="fixed inset-0 z-[1400] flex items-center justify-center bg-black/40 p-3">
          <div className="bg-white rounded-xl p-4 w-full max-w-md shadow-lg">
            <div className="font-semibold mb-1">Draw signature</div>
            <div className="text-xs text-[#666] mb-2">
              Draw below, then Insert — it appears on the form so you can drag
              it into place.
            </div>
            <canvas
              ref={initSignatureCanvas}
              width={480}
              height={180}
              className="w-full border border-solid border-[#DDD] rounded-md touch-none cursor-crosshair"
              onPointerDown={(e) => signaturePointer(e, "down")}
              onPointerMove={(e) => signaturePointer(e, "move")}
              onPointerUp={(e) => signaturePointer(e, "up")}
              onPointerLeave={(e) => signaturePointer(e, "up")}
            />
            <div className="flex justify-end gap-2 mt-3">
              <button
                type="button"
                className="px-4 py-1.5 rounded-full text-sm border border-solid border-[#999] bg-white cursor-pointer"
                onClick={clearSignature}
              >
                Clear
              </button>
              <button
                type="button"
                className="px-4 py-1.5 rounded-full text-sm border border-solid border-[#999] bg-white cursor-pointer"
                onClick={() => setSignatureOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="px-4 py-1.5 rounded-full text-sm border border-solid border-[#017C57] bg-[#017C57] text-white cursor-pointer"
                onClick={insertSignature}
              >
                Insert signature
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FillTemplateModal;
